import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { TimeSpan, type ITimeSpan } from '@/objects/TimeSpan';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class StationAlias extends BaseObj {
    public ID: number;
    public __id: number;

    public ActiveFrom: Date | null;
    public __activefrom: Date | null;

    public ActiveTo: Date | null;
    public __activeto: Date | null;

    public Alias: string | null;
    public __alias: string | null;

    public readonly AliasWithoutEtsChannel: string | null;

    public BibOffset: boolean;
    public __biboffset: boolean;

    public ReadsBib: boolean;
    public __readsbib: boolean;

    public ReadsChip: boolean;
    public __readschip: boolean;

    public StationID: number;
    public __stationid: number;

    public Timeshift: ITimeSpan;
    public __timeshift: ITimeSpan;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        ActiveFrom?: Date | null;
        ActiveTo?: Date | null;
        Alias?: string | null;
        AliasWithoutEtsChannel?: string | null;
        BibOffset?: boolean;
        EntityState?: EntityState;
        ReadsBib?: boolean;
        ReadsChip?: boolean;
        StationID?: number;
        Timeshift?: ITimeSpan;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<StationAlias>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('StationAlias tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.ActiveFrom = values.ActiveFrom ?? null;
        this.__activefrom = values.ActiveFrom ?? null;
        Object.defineProperty(this, 'ActiveFrom', {
            get(): Date | null {
                return this.__activefrom;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('ActiveFrom', this.__activefrom, value);
                this.__activefrom = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ActiveTo = values.ActiveTo ?? null;
        this.__activeto = values.ActiveTo ?? null;
        Object.defineProperty(this, 'ActiveTo', {
            get(): Date | null {
                return this.__activeto;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('ActiveTo', this.__activeto, value);
                this.__activeto = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Alias = values.Alias ?? null;
        this.__alias = values.Alias ?? null;
        Object.defineProperty(this, 'Alias', {
            get(): string | null {
                return this.__alias;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Alias', this.__alias, value);
                this.__alias = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.AliasWithoutEtsChannel = values.AliasWithoutEtsChannel ?? null;

        this.BibOffset = values.BibOffset ?? false;
        this.__biboffset = values.BibOffset ?? false;
        Object.defineProperty(this, 'BibOffset', {
            get(): boolean {
                return this.__biboffset;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('BibOffset', this.__biboffset, value);
                this.__biboffset = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ReadsBib = values.ReadsBib ?? false;
        this.__readsbib = values.ReadsBib ?? false;
        Object.defineProperty(this, 'ReadsBib', {
            get(): boolean {
                return this.__readsbib;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ReadsBib', this.__readsbib, value);
                this.__readsbib = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ReadsChip = values.ReadsChip ?? false;
        this.__readschip = values.ReadsChip ?? false;
        Object.defineProperty(this, 'ReadsChip', {
            get(): boolean {
                return this.__readschip;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ReadsChip', this.__readschip, value);
                this.__readschip = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StationID = values.StationID ?? 0;
        this.__stationid = values.StationID ?? 0;
        Object.defineProperty(this, 'StationID', {
            get(): number {
                return this.__stationid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('StationID', this.__stationid, value);
                this.__stationid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Timeshift = values.Timeshift ?? TimeSpan.Empty();
        this.__timeshift = values.Timeshift ?? TimeSpan.Empty();
        Object.defineProperty(this, 'Timeshift', {
            get(): ITimeSpan {
                return this.__timeshift;
            },
            set(value: ITimeSpan) {
                this.__entityStateHandler.propertyChanged('Timeshift', this.__timeshift, value);
                this.__timeshift = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"ActiveFrom": ${this.ActiveFrom ? '"' + formatDateIso(this.ActiveFrom) + '"' : null}` +
            ',' +
            `"ActiveTo": ${this.ActiveTo ? '"' + formatDateIso(this.ActiveTo) + '"' : null}` +
            ',' +
            `"Alias": ${JSON.stringify(this.Alias)}` +
            ',' +
            `"AliasWithoutEtsChannel": ${JSON.stringify(this.AliasWithoutEtsChannel)}` +
            ',' +
            `"BibOffset": ${JSON.stringify(this.BibOffset)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"ReadsBib": ${JSON.stringify(this.ReadsBib)}` +
            ',' +
            `"ReadsChip": ${JSON.stringify(this.ReadsChip)}` +
            ',' +
            `"StationID": ${JSON.stringify(this.StationID)}` +
            ',' +
            `"Timeshift": ${'"' + this.Timeshift?.serialize() + '"'}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of StationAlias from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        ActiveFrom?: Date | null;
        ActiveTo?: Date | null;
        Alias?: string | null;
        BibOffset?: boolean;
        ReadsBib?: boolean;
        ReadsChip?: boolean;
        StationID?: number;
        Timeshift?: ITimeSpan;
        EntityState?: 'Added' | 'Unchanged';
    }): StationAlias {
        return new StationAlias(values);
    }

    public static _FromServerJson(json: any): StationAlias {
        return new StationAlias({
            ID: json.ID,
            ActiveFrom: json.ActiveFrom ? (json.ActiveFrom instanceof Date ? json.ActiveFrom : new Date(json.ActiveFrom)) : null,
            ActiveTo: json.ActiveTo ? (json.ActiveTo instanceof Date ? json.ActiveTo : new Date(json.ActiveTo)) : null,
            Alias: json.Alias ?? '',
            AliasWithoutEtsChannel: json.AliasWithoutEtsChannel ?? '',
            BibOffset: json.BibOffset,
            ReadsBib: json.ReadsBib,
            ReadsChip: json.ReadsChip,
            StationID: json.StationID,
            Timeshift: json.Timeshift ? TimeSpan.FromTimeString(json.Timeshift) : TimeSpan.Empty(),
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of StationAlias from another instance of StationAlias.
     * @param other The StationAlias you want to clone
     * @returns A cloned StationAlias
     */
    public static FromOther(other: StationAlias): StationAlias {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return StationAlias._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return StationAlias.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'ActiveFrom'
            | 'ActiveTo'
            | 'Alias'
            | 'AliasWithoutEtsChannel'
            | 'BibOffset'
            | 'ReadsBib'
            | 'ReadsChip'
            | 'StationID'
            | 'Timeshift'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'StationAlias';
}
